import clsx from "clsx";
import CheckoutBar from "@app/components/ui/checkout-bar";
import dayjs from "dayjs";
import { Cloud } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import { hasQueue, sessionHours } from "./cloud-catalog";
import { useT } from "@app/contexts/preferences.context";
import { IcCloud, IcWallet } from "@app/components/icons";
import { btn, btnGhost, btnLg, btnPrimary, input, specMain } from "@app/components/ui";
import { PathnameUrl } from "@app/constants";

interface CloudSummaryProps {
  cloud?: Cloud;
  discount: number;
  balance: number;
  name: string;
  agree: boolean;
  /** đã bấm Tạo mà thiếu tên / chưa đồng ý điều khoản */
  showErrors: boolean;
  busy: boolean;
  onName: (v: string) => void;
  onAgree: (v: boolean) => void;
  onUnpick: () => void;
  onCreate: () => void;
  /** Mở trang Nạp tiền với số tiền còn thiếu */
  onTopup: (need: number) => void;
}

const FAQ: [string, string][] = [
  [
    "Hàng chờ là gì?",
    "Gói có hàng chờ dùng chung máy chủ: khi bấm Bật máy mà máy đang đầy, bạn xếp hàng và máy tự bật khi tới lượt. Gói không hàng chờ luôn bật được ngay.",
  ],
  [
    "Giờ chơi và hạn sử dụng khác nhau thế nào?",
    "Giờ chơi là số giờ bạn được dùng máy, dùng hết thì phải gia hạn. Hạn sử dụng là ngày cuối cùng được dùng số giờ đó: tới hạn mà vẫn còn giờ thì đơn hàng cũng kết thúc.",
  ],
  [
    "Session là gì?",
    "Mỗi lần bấm Bật máy là một session và bắt đầu tính giờ. Nếu không tắt, tới hết thời gian session máy tự tắt và lưu dữ liệu, muốn chơi tiếp bạn bấm Bật máy lại.",
  ],
  [
    "Dữ liệu được lưu thế nào?",
    "Game và phần mềm bạn cài nằm trong Image Cloud, được lưu sau mỗi session cho tới khi Image hết hạn.",
  ],
];

/** Khung "Thông tin tạo Image" (thay cho CreateCloudModal cũ). */
function CloudSummary({
  cloud,
  discount,
  balance,
  name,
  agree,
  showErrors,
  busy,
  onName,
  onAgree,
  onUnpick,
  onCreate,
  onTopup,
}: CloudSummaryProps) {
  const t = useT();
  const money = cloud ? cloud.price - (cloud.price * discount) / 100 : 0;
  const lack = money - balance;
  const nameError = showErrors && !name.trim();
  const agreeError = showErrors && !agree;

  return (
    <>
      <aside id="cloud-summary" className="sum-box sum-abs flex min-h-[520px] flex-col self-stretch overflow-hidden rounded-[14px] border border-line bg-surface shadow-card max-[1100px]:min-h-0">
        {/* khung cao bằng cột gói, phần giữa cuộn bên trong như bản thiết kế (.sum-abs) */}
        <div className="sum-in">
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
            <h2 className="text-[15px] font-bold">{t("Thông tin tạo Image")}</h2>
            {cloud && (
              <button type="button" className={clsx(btn, btnGhost, "!h-8")} onClick={onUnpick}>
                {t("Bỏ chọn")}
              </button>
            )}
          </div>

          {!cloud ? (
        <ol className="m-0 flex flex-1 list-none flex-col gap-3.5 px-4 py-3">
          {[
            ["Chọn gói", "Bấm vào một gói ở bên trái."],
            ["Đặt tên Image", "Để nhận ra Image trong danh sách."],
            ["Tạo Image", "Trừ tiền từ số dư, dùng được ngay."],
          ].map(([title, desc], i) => (
            <li key={title} className="grid grid-cols-[28px_1fr] items-start gap-2.5">
              <span
                className={clsx(
                  "grid h-7 w-7 place-items-center rounded-full border text-[13px] font-bold",
                  i === 0 ? "border-brand bg-brand text-white" : "border-line bg-surface-2 text-muted"
                )}
              >
                {i + 1}
              </span>
              <div>
                <b className="block font-semibold">{t(title)}</b>
                <span className="text-[13px] text-muted">{t(desc)}</span>
              </div>
            </li>
          ))}
        </ol>
          ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-2.5">
            <div className="flex items-center gap-2.5 rounded-[10px] bg-surface-2 px-2 py-1.5">
              <span className="grid h-9 w-9 flex-none place-items-center rounded-lg pc-thumb [--glow:var(--brand)] text-brand">
                <IcCloud className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-bold">{cloud.name}</h3>
                <p className="m-0 truncate text-xs text-muted">
                  {specMain(cloud.vga)} · {specMain(cloud.ram)} RAM
                </p>
              </div>
            </div>

            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
              {t("Tên Image")}
              <input
                className={clsx(input, nameError && "!border-bad")}
                maxLength={32}
                placeholder={t("Nhập tên Image Cloud")}
                value={name}
                onChange={(e) => onName(e.target.value)}
              />
              {nameError ? (
                <small className="font-medium text-bad">{t("Nhập tên Image trước khi tạo.")}</small>
              ) : (
                <small className="font-normal text-faint">{t("Ví dụ: Valorant + OBS")}</small>
              )}
            </label>

            <div className="flex flex-col gap-1 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px] tabular-nums">
              {(
                [
                  ["Giờ chơi", `${cloud.hours_of_use} ${t("giờ")}`],
                  [
                    "Hạn sử dụng",
                    `${cloud.days_of_use * 24} ${t("giờ")}, ${t("đến")} ${dayjs().add(cloud.days_of_use, "day").format("DD/MM/YYYY")}`,
                  ],
                  [
                    "Mỗi session",
                    sessionHours(cloud)
                      ? `${t("tự tắt sau")} ${sessionHours(cloud)} ${t("giờ")}`
                      : t("Không giới hạn"),
                  ],
                  ["Hàng chờ", hasQueue(cloud) ? t("Có hàng chờ") : t("Không hàng chờ")],
                ] as [string, string][]
              ).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <span className="text-muted">{t(k)}</span>
                  <b className="text-right">{v}</b>
                </div>
              ))}
            </div>

            <div className="flex flex-col overflow-hidden rounded-[10px] border border-line">
              {/* {FAQ.map(([q, a], i) => (
                <details key={q} className={clsx("group", i > 0 && "border-t border-line")}>
                  <summary className="flex cursor-pointer list-none justify-between gap-2 px-3 py-2 text-[12.5px] font-semibold after:font-bold after:text-faint after:content-['+'] group-open:after:content-['–'] [&::-webkit-details-marker]:hidden">
                    {t(q)}
                  </summary>
                  <p className="m-0 px-3 pb-2.5 text-[12.5px] leading-snug text-muted">{t(a)}</p>
                </details>
              ))} */}
            </div>
          </div>

          <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-2.5">
            <div className="flex items-baseline justify-between rounded-[10px] bg-surface-2 px-3 py-2.5 tabular-nums">
              <span className="font-semibold">{t("Thành tiền")}</span>
              <span>
                {discount > 0 && <s className="mr-1.5 text-xs text-faint">{formatMoneyVN(cloud.price)}</s>}
                <strong className="text-lg text-brand">{formatMoneyVN(money)} VND</strong>
              </span>
            </div>
            <label className={clsx("flex cursor-pointer items-start gap-2 text-[12.5px] leading-snug", agreeError ? "text-bad" : "text-muted")}>
              <input
                type="checkbox"
                className="mt-px h-4 w-4 flex-none accent-[var(--brand)]"
                checked={agree}
                onChange={(e) => onAgree(e.target.checked)}
              />
              <span>
                {t("Tôi hiểu gói không được hoàn tiền sau khi tạo và đồng ý")}{" "}
                <a
                  className="font-semibold text-brand"
                  href={PathnameUrl.TERMS}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("điều khoản sử dụng")}
                </a>
                .
              </span>
            </label>
            {lack > 0 ? (
              <button type="button" className={clsx(btn, btnPrimary, btnLg, "w-full")} onClick={() => onTopup(lack)}>
                <IcWallet className="h-[18px] w-[18px]" />
                {t("Nạp thêm")} {formatMoneyVN(lack)} VND
              </button>
            ) : (
              <button type="button" className={clsx(btn, btnPrimary, btnLg, "w-full")} disabled={busy} onClick={onCreate}>
                <IcCloud className="h-[18px] w-[18px]" />
                {busy ? t("Đang tạo…") : `${t("Tạo Image")} · ${formatMoneyVN(money)}đ`}
              </button>
            )}
          </div>
        </>
          )}
        </div>
      </aside>
      <CheckoutBar
        targetId="cloud-summary"
        active={!!cloud}
        name={cloud ? `${cloud.name} · ${cloud.hours_of_use} ${t("giờ chơi")}` : ""}
        total={`${formatMoneyVN(money)} VND`}
      />
    </>
  );
}

export default CloudSummary;
